import { Stack, Typography } from '@mui/material';
import { GridColDef, GridRow } from '@mui/x-data-grid-pro';

import AppLink from '@/components/AppLink';
import DataGrid from '@/components/DataGrid';
import { BasetenPageEnum } from '@/routes';
import VersionId from '@/sections/Workspace/components/VersionId';
import moment from 'moment';

import { dataGridProps, dataGridStyles } from '../../styled';
import Actions from '../Actions';
import { WorkspaceTrainingProjectFragment } from '../__generated__/fragments.generated';
import { MAX_ITEMS_TO_SHOW } from '../constants';
import { usePinnedAPI } from '../hooks';
import TrainingJobsCountByStatusToChips from '../sections/TrainingJobsCountByStatusToChips';
import { ListProps } from '../types';

const ROW_HEIGHT = 67;

function Table({ trainingProjects, height, minimal }: ListProps) {
  const { getPinActionItem } = usePinnedAPI();

  const columns: GridColDef<WorkspaceTrainingProjectFragment>[] = [
    {
      field: 'name',
      headerName: 'Name',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: ({ row }) => (
        <Stack width="100%" gap={1} direction="row" alignItems="center">
          <Stack gap={0.5} justifyContent="center">
            <Stack direction="row" gap={1}>
              <Typography noWrap variant="h5">
                {row.name}
              </Typography>
              <VersionId id={row.id} containerProps={{ variant: 'h5' }} />
            </Stack>
            <Typography color="text.subtle" variant="body2">
              Created {moment(row.created).format('M/D [at] h:mm:ss A')} by {row.user.name} • Last
              run on {moment(row.modified).format('M/D [at] h:mm:ss A')}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'jobs',
      headerName: 'Jobs',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 230,
      renderCell: ({ row }: { row: WorkspaceTrainingProjectFragment }) => (
        <TrainingJobsCountByStatusToChips jobsCountByStatus={row.jobsCountByStatus} />
      ),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" gap={0.2}>
            {getPinActionItem(row.id, row.name)}
            <Actions trainingProject={row} variant="minimal" />
          </Stack>
        );
      },
    },
  ];

  return (
    <Stack data-cy="training-table-content" height={minimal ? 'auto' : height}>
      <DataGrid<WorkspaceTrainingProjectFragment>
        rows={minimal ? trainingProjects.slice(0, MAX_ITEMS_TO_SHOW) : trainingProjects}
        columns={columns}
        rowHeight={ROW_HEIGHT}
        getRowId={(row) => row.id}
        sx={dataGridStyles}
        // Row virtualization does not work with the autoHeight prop enabled.
        // https://mui.com/x/react-data-grid/virtualization/#row-virtualization
        disableAutoHeight={!minimal}
        {...dataGridProps}
        slots={{
          row: (props) => (
            <AppLink
              page={BasetenPageEnum.TrainingProject}
              params={{ trainingProjectId: props.row.id }}
              key={props.row}
              underline="none"
              sx={{ color: 'inherit', '&:hover': { textDecoration: 'none' } }}
            >
              <GridRow {...props} />
            </AppLink>
          ),
        }}
      />
    </Stack>
  );
}

export default Table;
